import { Navigate, Route, Routes, useLocation } from "react-router";
import { Spinner } from "@fluentui/react-components";
import { useAuth } from "./lib/auth.tsx";
import { Shell } from "./components/Shell.tsx";
import { LoginPage } from "./pages/Login.tsx";
import { RegisterPage } from "./pages/Register.tsx";
import { DashboardPage } from "./pages/Dashboard.tsx";
import { AvailabilityPage } from "./pages/Availability.tsx";
import { ClientsPage } from "./pages/Clients.tsx";
import { ClientDetailPage } from "./pages/ClientDetail.tsx";
import { SchedulePage } from "./pages/Schedule.tsx";
import { SettingsPage } from "./pages/Settings.tsx";
import { JoinPage } from "./pages/Join.tsx";
import { AdminPage } from "./pages/Admin.tsx";
import { ClientPortalPage } from "./pages/ClientPortal.tsx";
import { LegalPage } from "./pages/Legal.tsx";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/ResetPassword.tsx";
import { LandingPage } from "./pages/Landing.tsx";
import { SetupPage } from "./pages/Setup.tsx";

function RequireAuth({ children, roles }: { children: React.ReactElement; roles?: string[] }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Centered><Spinner label="Loading…" /></Centered>;
  // Signed-out visitors hitting the root get the public landing page; deeper links go to sign-in.
  if (!user && loc.pathname === "/") return <LandingPage />;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  // New trainers finish the setup chat before anything else (never land on a blank calendar).
  if (user.trainer && !user.trainer.onboarded && loc.pathname !== "/setup") return <Navigate to="/setup" replace />;
  return children;
}

export function Centered({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>{children}</div>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/me/:token" element={<ClientPortalPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/privacy/:doc" element={<LegalPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/setup" element={<RequireAuth roles={["TRAINER", "ADMIN"]}><SetupPage /></RequireAuth>} />
      <Route
        path="/"
        element={
          <RequireAuth roles={["TRAINER", "ADMIN"]}>
            <Shell />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="availability" element={<AvailabilityPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/:id" element={<ClientDetailPage />} />
        <Route path="schedule" element={<SchedulePage />} />
        <Route path="checkins" element={<Navigate to="/schedule" replace />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route
          path="admin"
          element={
            <RequireAuth roles={["ADMIN"]}>
              <AdminPage />
            </RequireAuth>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
